import { useLayoutEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { useFeatureFlagEnabled } from "posthog-js/react";
import Button from "components/Button";
import ViewAsCustomerToggle from "components/ViewAsCustomerToggle";
import { useFirebase } from "firebase";
import { useGET } from "hooks/useAPI";
import { useIsCareSpecialist } from "hooks/useIsCareSpecialist";
import { useScrollPageToTop } from "context/PageScrollContext";

import {
  isArrangementAccessibleStage,
  canAccessArrangementFlow,
  getArrangementStatus,
  isArrangementLocked,
} from "helpers/at-need";

import { ArrangementProvider, useArrangementState } from "./ArrangementContext";
import ArrangementProgressBar from "./ArrangementProgressBar";
import AdjustOrderDrawer from "./AdjustOrderDrawer";
import HelpFooter from "./HelpFooter";
import ArrangementLoading from "./ArrangementLoading";
import ArrangementError from "./ArrangementError";
import ArrangementAccessDenied from "./ArrangementAccessDenied";
import ArrangementNotReady from "./ArrangementNotReady";
import { STEP_ROUTES } from "./constants";

export default function ArrangementFlow() {
  const { caseId } = useParams<{ caseId: string }>();
  const { user: firebaseUser } = useFirebase();
  const location = useLocation();
  const isArrangementFlowEnabled = useFeatureFlagEnabled(
    "at-need-arrangement-flow",
  );
  const isAfterUser = !!firebaseUser?.email?.endsWith("@after.com");
  const isCareSpecialist = useIsCareSpecialist();

  // Read case stage to gate the route on Dispatch/Care eligibility for
  // case contacts. @after.com users skip this fetch — they always have access.
  const {
    data: caseData,
    error: caseDataError,
    isLoading: isLoadingCase,
  } = useGET(caseId && !isAfterUser ? `/cases/${caseId}` : null, undefined, {
    shouldRetryOnError: (error: any) => error?.statusCode !== 404,
  });

  const stage = (caseData as { stage?: string } | undefined)?.stage;

  // For stages with no direct arrangement access, also fetch app-data so a
  // family with an existing draft can still finish even if CS advanced the
  // case past Docs while they were mid-flow. Access stages (Dispatch/Care/
  // Docs) skip this fetch to avoid extra latency on the happy path.
  const shouldCheckArrangementRecord =
    !!caseId &&
    !isAfterUser &&
    !!caseData &&
    !isArrangementAccessibleStage(stage);
  const {
    data: appData,
    error: appDataError,
    isLoading: isLoadingAppData,
  } = useGET(
    shouldCheckArrangementRecord ? `/cases/${caseId}/app-data` : null,
    undefined,
    { shouldRetryOnError: (error: any) => error?.statusCode !== 404 },
  );

  // Gate the entire route behind the at-need-arrangement-flow flag.
  // When the flag is off, this route is effectively absent (parity with main).
  // @after.com users can always access the flow — it's a preview/review tool
  // for them — even with ?view-as-customer, which only toggles the UI mode.
  if (isArrangementFlowEnabled === false && !isAfterUser) {
    return <Navigate to={`/cases/${caseId}${location.search}`} replace />;
  }
  if (isArrangementFlowEnabled === undefined && !isAfterUser) {
    return <ArrangementLoading />;
  }

  // Stage gate: case contacts reach the flow while the case is in an
  // arrangement-access stage (Dispatch/Care/Docs), OR when an arrangement
  // record already exists (so a family mid-draft isn't locked out if CS
  // advances the case further). The timeline CTA derives from the same
  // canAccessArrangementFlow predicate, so the two can never disagree and
  // trap the family in a redirect loop.
  // Wait for the case fetch to resolve before deciding so we don't bounce
  // on first paint. If the case fetch errors (no data after load), fall
  // through and let the inner provider surface its standard error UI.
  if (!isAfterUser) {
    if (isLoadingCase) {
      return <ArrangementLoading />;
    }
    // No access to this case — the /:caseId API 404s anyone who isn't a contact
    // on it. Almost always a stale bookmark or a link to the wrong case, so send
    // them to the homepage (which routes them to where they belong) rather than
    // mounting the flow and surfacing an error screen.
    if (
      (caseDataError as { statusCode?: number } | undefined)?.statusCode === 404
    ) {
      return <Navigate to="/" replace />;
    }
    if (caseData && !isArrangementAccessibleStage(stage)) {
      // Stage with no direct access. Allow in only when a draft/saved
      // arrangement record exists. Distinguish 404 (= confirmed no record,
      // bounce) from other errors (= transient backend issue, fail open and
      // let the inner provider surface its standard error UI rather than
      // bouncing silently).
      if (isLoadingAppData) {
        return <ArrangementLoading />;
      }
      const isAppDataNotFound =
        (appDataError as { statusCode?: number } | undefined)?.statusCode ===
        404;
      const isTransientAppDataError = !!appDataError && !isAppDataNotFound;
      // SWR can briefly report not-loading with no data and no error on a
      // cold key activation; fail open on that (and on transient errors)
      // rather than reading it as "no record" and bouncing.
      const isAppDataUnresolved = appData === undefined && !appDataError;
      const shouldFailOpen = isTransientAppDataError || isAppDataUnresolved;
      if (
        !shouldFailOpen &&
        !canAccessArrangementFlow(stage, getArrangementStatus(appData))
      ) {
        return <Navigate to={`/cases/${caseId}${location.search}`} replace />;
      }
    }
  }

  return (
    <ArrangementProvider key={caseId} isCareSpecialist={isCareSpecialist}>
      <ArrangementFlowInner />
    </ArrangementProvider>
  );
}

/** Jumps to the top whenever the step route changes, whatever triggered it —
 * the nav buttons, the progress bar, or the review drawer. Rendered inside
 * the portal shell so it scrolls the shell's own scroll container. */
function ScrollToTopOnStepChange() {
  const { pathname } = useLocation();
  const scrollPageToTop = useScrollPageToTop();

  // Layout effect so the new step never paints at the old step's position.
  useLayoutEffect(() => {
    scrollPageToTop();
  }, [pathname, scrollPageToTop]);

  return null;
}

function ArrangementFlowInner() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { state, config, isLoading, hasInitialized, error, isReadOnly } =
    useArrangementState();
  const { caseId } = useParams<{ caseId: string }>();
  const { search } = useLocation();

  const step = state.currentStep;
  const isConfirmation = step === STEP_ROUTES.indexOf("confirmation");
  const isEarlyStep = step <= STEP_ROUTES.indexOf("packages");
  const hasSelections = !!state.selectedPackageId;
  const showReview = !isConfirmation && (!isEarlyStep || hasSelections);

  if (isLoading) return <ArrangementLoading />;
  if (error?.type === "access_denied") return <ArrangementAccessDenied />;
  if (error) return <ArrangementError />;
  // Persisted selections merge in a post-commit effect; rendering before that
  // briefly drops selected-but-filtered add-ons.
  if (!hasInitialized) return <ArrangementLoading />;

  if (config && !config.case_context?.dcd_location) {
    return <ArrangementNotReady />;
  }

  const isSubmitted = isArrangementLocked(config?.arrangement_status);

  // Once the arrangement is submitted, Basecamp Hub owns the next step
  // (Consultation unlock or Timeline when everything is complete).
  if (isSubmitted && !isReadOnly) {
    return <Navigate to={`/cases/${caseId}${search}`} replace />;
  }

  // CS read-only + normal editable flow share the same layout
  const showReviewButton = !isReadOnly && showReview;

  return (
    <>
      <ScrollToTopOnStepChange />
      <div className="flex flex-1 flex-col">
        <div className="mx-auto w-full max-w-5xl pt-6 sm:pt-8">
          {showReviewButton && (
            <div className="flex justify-end px-4 pb-4 sm:px-6 lg:px-8">
              <Button
                onClick={() => setDrawerOpen(true)}
                variant="outline"
                size="sm"
              >
                Review
              </Button>
            </div>
          )}
          <ArrangementProgressBar />
        </div>

        {/* Step content. Below sm the nav is a fixed bottom bar and the
            HelpFooter follows in normal flow, so we don't stretch the content
            (which would push the footer to the bottom and leave a gap on short
            steps). At sm+ the nav is inline, so flex-1 keeps the footer at the
            bottom. Both switches share the sm breakpoint so the footer never
            floats mid-screen at tablet widths. */}
        <div className="mx-auto w-full max-w-5xl px-4 pb-8 sm:flex-1 sm:px-6 sm:pb-24 lg:px-8">
          <Outlet />
        </div>

        <HelpFooter />
      </div>

      {!isReadOnly && (
        <AdjustOrderDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />
      )}

      <ViewAsCustomerToggle />
    </>
  );
}
